<template>
  <div class="part-editor-page" :aria-busy="getPartLoading == true ? 'true' : 'false'">
    <Loader v-if="getPartLoading == true" />

    <LoadingOverlay
      v-if="checkEdit() && modernDetailLoading"
      mode="block"
      compact
      clear
      :min-height="300"
    />

    <div v-else-if="checkEdit() && modernDetailError" class="part-editor-detail-error" role="alert">
      <div>
        <strong>Could not load this catalog part</strong>
        <p>{{ modernDetailError }}</p>
      </div>
      <button type="button" class="part-editor-button" @click="loadModernPartDetail({ initial: true })">Try again</button>
    </div>

    <div v-show="!checkEdit() || (!modernDetailLoading && !modernDetailError)" class="part-editor-loaded-content">

    <div class="part-editor-overview">
      <div class="part-editor-overview-copy">
        <span class="part-editor-kicker">Catalog record</span>
        <strong>{{ checkEdit() && editorPart ? (editorPart.model || editorPart.description || 'Existing part') : 'New catalog part' }}</strong>
        <small>Manage source media, merchandising copy, specifications, and compatibility values.</small>
      </div>
      <div class="part-editor-actions">
        <a
          v-if="generalValues && amazonLink(generalValues[0])"
          target="_blank"
          rel="noopener noreferrer"
          class="part-editor-button part-editor-button-primary"
          :href="amazonLink(generalValues[0])"
        >
          <span class="icon icon-link"></span>
          <span>Amazon</span>
        </a>
        <button
          v-if="checkEdit()"
          type="button"
          class="part-editor-button"
          :disabled="getPartLoading == true"
          @click="handleProcessorSubmit({clone: true})"
        >
          <span class="icon icon-documents"></span>
          <span>Clone part</span>
        </button>
      </div>
    </div>

    <div v-if="modernDetailRefreshing" class="part-editor-refreshing" aria-live="polite">
      Refreshing the part details&hellip;
    </div>

    <PartMaintenancePanel
      v-if="checkEdit() && modernPart"
      :actions="maintenanceActions"
      :part="editorPart"
      :part-type="modernPartType"
      :part-id="modernPartId"
      @completed="handleMaintenanceCompleted"
    />

    <section v-if="checkEdit() && evaluationItem" class="part-editor-evaluation-card">
      <SpecEvaluationPanel
        :item="evaluationItem"
        health-title="Catalog health"
        health-description="Structured checks and findings returned for this catalog part."
        :show-fields="false"
        :show-research="false"
        :show-research-all="false"
        :show-verify-diff="false"
      />
    </section>

    <section v-if="checkEdit() && editorialFacts.length" class="part-editor-card part-editor-editorial-card">
      <header class="part-editor-section-head">
        <div>
          <span class="part-editor-section-kicker">AI editorial</span>
          <h2>Catalog content</h2>
        </div>
        <span class="part-editor-readonly">Read only</span>
      </header>
      <div class="part-editor-editorial-grid">
        <article v-for="fact in editorialFacts" :key="fact.key" :class="{ 'is-wide': fact.wide }">
          <span>{{ fact.label }}</span>
          <a v-if="fact.url" :href="fact.url" target="_blank" rel="noopener noreferrer">{{ fact.value }}</a>
          <strong v-else>{{ fact.value }}</strong>
        </article>
      </div>
    </section>

    <form class="part-editor-main" @submit.prevent="handleProcessorSubmit({clone: false})">
      <fieldset class="part-editor-fieldset edit" :disabled="getPartLoading == true">
        <div class="part-editor-assets-grid">
          <section class="part-editor-card part-editor-media-card">
            <header class="part-editor-section-head">
              <div>
                <span class="part-editor-section-kicker">Source media</span>
                <h2>Images</h2>
              </div>
              <span class="part-editor-count">{{ images && images !== 'N/A' ? images.length : 0 }}</span>
            </header>

            <div v-if="images && images !== 'N/A' && images.length" class="part-editor-image-grid">
              <div
                v-for="(img, index) in images"
                :key="index"
                class="part-editor-image"
                :class="{ 'is-main': index === 0 }"
                :style="{backgroundImage: 'url(' + img + ')'}"
              >
                <img
                  class="part-editor-image-probe"
                  :src="img"
                  alt=""
                  @load="rememberImageResolution(img, $event)"
                  @error="rememberImageResolutionError(img)"
                >
                <span v-if="index === 0" class="part-editor-main-badge">Main</span>
                <span class="part-editor-image-resolution" :class="{ 'with-action': index !== 0 }">
                  {{ imageResolution(img) }}
                </span>
                <button
                  type="button"
                  class="part-editor-image-remove"
                  :aria-label="'Remove image ' + (index + 1)"
                  @click="spliceImg(index)"
                >
                  &times;
                </button>
                <button
                  v-if="index !== 0"
                  type="button"
                  class="part-editor-make-main"
                  @click="makeMain(images, index)"
                >
                  Make main
                </button>
              </div>
            </div>
            <button v-else type="button" class="part-editor-empty-media" @click="imgInputFocus()">
              <span class="icon icon-image"></span>
              <strong>No product images yet</strong>
              <small>Paste an image URL below to add the first one.</small>
            </button>

            <div class="part-editor-inline-add">
              <input
                id="imgInput"
                ref="imgInput"
                v-model="imgUrl"
                type="text"
                class="form-control url-input"
                placeholder="Paste image URL"
              >
              <button type="button" class="part-editor-button part-editor-button-primary" @click="addImg(imgUrl)">Add image</button>
            </div>
          </section>

          <section class="part-editor-card part-editor-bullets-card">
            <header class="part-editor-section-head">
              <div>
                <span class="part-editor-section-kicker">Merchandising copy</span>
                <h2>Bullet points</h2>
              </div>
              <span class="part-editor-count">{{ bullets.length }}</span>
            </header>

            <ul v-if="bullets.length" class="part-editor-bullet-list">
              <li v-for="(bulletItem, index) in bullets" :key="index">
                <span>{{ bulletItem }}</span>
                <button type="button" :aria-label="'Remove bullet point ' + (index + 1)" @click="spliceBullet(index)">&times;</button>
              </li>
            </ul>
            <div v-else class="part-editor-empty-copy">No bullet points have been added.</div>

            <div class="part-editor-inline-add">
              <input
                ref="bulletInput"
                v-model="bullet"
                type="text"
                class="form-control url-input"
                placeholder="Add a bullet point"
              >
              <button type="button" class="part-editor-button part-editor-button-primary" @click="addBullet(bullet)">Add bullet</button>
            </div>
          </section>
        </div>

        <div class="part-editor-form-groups">
          <section class="part-editor-card part-editor-group part-editor-group-general">
            <header class="part-editor-section-head">
              <div>
                <span class="part-editor-section-kicker">Core record</span>
                <h2>General information</h2>
              </div>
              <span class="part-editor-count">{{ forms.attribs.general.length }} fields</span>
            </header>
            <div class="part-editor-fields part-editor-fields-general">
              <div v-for="(field, index) in forms.attribs.general" :key="field" class="part-editor-field">
                <label :for="'general-' + index">{{ fieldLabel(field) }}</label>
                <input
                  v-if="field == 'type'"
                  :id="'general-' + index"
                  :ref="'input_general_' + field"
                  v-model="type"
                  type="text"
                  class="form-control"
                  :placeholder="message"
                >
                <select
                  v-else-if="dropdown(getType(), field).length > 0"
                  :id="'general-' + index"
                  :ref="'input_general_' + field"
                  v-model="generalValues[index]"
                  :name="field"
                  class="form-control"
                >
                  <option v-for="(option, optIndex) in dropdown(getType(), field)" :key="optIndex" :value="option">{{ option }}</option>
                </select>
                <textarea
                  v-else-if="isLongTextField(field)"
                  :id="'general-' + index"
                  :ref="'input_general_' + field"
                  v-model="generalValues[index]"
                  class="form-control part-editor-textarea"
                  :placeholder="message"
                  rows="3"
                ></textarea>
                <input
                  v-else
                  :id="'general-' + index"
                  :ref="'input_general_' + field"
                  v-model="generalValues[index]"
                  type="text"
                  class="form-control"
                  :placeholder="message"
                >
              </div>
            </div>
          </section>

          <section class="part-editor-card part-editor-group">
            <header class="part-editor-section-head">
              <div>
                <span class="part-editor-section-kicker">Technical data</span>
                <h2>Specifications</h2>
              </div>
              <span class="part-editor-count">{{ forms.attribs.specifications.length }} fields</span>
            </header>
            <div class="part-editor-fields">
              <div v-for="(field, index1) in forms.attribs.specifications" :key="field" class="part-editor-field">
                <label :for="'spec-' + index1">{{ fieldLabel(field) }}</label>
                <select
                  v-if="dropdown(getType(), field).length > 0"
                  :id="'spec-' + index1"
                  :ref="'input_specs_' + field"
                  v-model="specValues[index1]"
                  :name="field"
                  class="form-control"
                >
                  <option v-for="(option, optIndex) in dropdown(getType(), field)" :key="optIndex" :value="option">{{ option }}</option>
                </select>
                <input
                  v-else
                  :id="'spec-' + index1"
                  :ref="'input_specs_' + field"
                  v-model="specValues[index1]"
                  type="text"
                  class="form-control"
                  :placeholder="message"
                >
              </div>
            </div>
          </section>

          <section class="part-editor-card part-editor-group">
            <header class="part-editor-section-head">
              <div>
                <span class="part-editor-section-kicker">Build rules</span>
                <h2>Compatibility</h2>
              </div>
              <span class="part-editor-count">{{ forms.attribs.compats.length }} fields</span>
            </header>
            <div class="part-editor-fields">
              <div v-for="(field, index2) in forms.attribs.compats" :key="field" class="part-editor-field">
                <label :for="'compat-' + index2">{{ fieldLabel(field) }}</label>
                <select
                  v-if="dropdown(getType(), field).length > 0"
                  :id="'compat-' + index2"
                  :ref="'input_compats_' + field"
                  v-model="compatsValues[index2]"
                  :name="field"
                  class="form-control"
                >
                  <option v-for="(option, optIndex) in dropdown(getType(), field)" :key="optIndex" :value="option">{{ option }}</option>
                </select>
                <input
                  v-else
                  :id="'compat-' + index2"
                  :ref="'input_compats_' + field"
                  v-model="compatsValues[index2]"
                  type="text"
                  class="form-control"
                  :placeholder="message"
                >
              </div>
            </div>
          </section>
        </div>

        <footer class="part-editor-save-bar">
          <div>
            <strong>{{ checkEdit() ? 'Save catalog changes' : 'Create catalog part' }}</strong>
            <small>{{ checkEdit() ? 'Updates the existing record with the values above.' : 'Creates a new record in this category.' }}</small>
          </div>
          <button type="submit" class="part-editor-button part-editor-button-save">
            {{ checkEdit() ? 'Save changes' : 'Create part' }}
          </button>
        </footer>
      </fieldset>
    </form>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions }   from 'vuex';
import Loader                       from '../widgets/loader.vue'
import LoadingOverlay               from '../widgets/LoadingOverlay.vue'
import PartMaintenancePanel         from './shared/PartMaintenancePanel.vue'
import SpecEvaluationPanel          from './shared/SpecEvaluationPanel.vue'
import _                            from 'lodash'
import axios                        from 'axios'
import {SERVER_PATH, amazonProductUrl, apiDomain} from '../../config'
import { normalizeBulletList }      from '../../utils/bullets'

export default {
  name: 'Add Part',
  components:{
    Loader,
    LoadingOverlay,
    PartMaintenancePanel,
    SpecEvaluationPanel
  },
  watch: { 
        values() { // watch it
          console.log('values changed')
        },
        asin: function(newVal) { // watch it
          console.log(newVal)
        },
        forms(){
          console.log('form was updated')
        }
    },
  data() {
    return {
      cleared: false,
      fetchingAmzn: false,
      recovered: false,
      images: [],
      imageDimensions: {},
      bullets: [],
      bullet: '',
      type: '123',
      status: 'Paste a product link and press fetch to get started...',
      forms: {},
      generalValues:  [],
      specValues:     [],
      compatsValues:   [],
      categoryChosen: false,
      category: '',
      message: '',
      modernPart: null,
      partEvaluation: null,
      maintenanceActions: [],
      modernDetailLoading: false,
      modernDetailRefreshing: false,
      modernDetailError: '',
      modernDetailRequestId: 0,
    }
  },
  computed: {
    editorPart () {
      return this.modernPart || this.getItem || null
    },
    modernPartId () {
      const part = this.editorPart || {}
      return this.$route.query.id || part.id || part.partId || part.part_id || ''
    },
    modernPartType () {
      const part = this.editorPart || {}
      return this.catalogPartType(this.$route.query.type || part.type || '')
    },
    evaluationItem () {
      if (!this.editorPart || !this.partEvaluation) return null
      return Object.assign({}, this.editorPart, { evaluation: this.partEvaluation })
    },
    editorContent () {
      const content = this.editorPart && this.editorPart.content
      return content && typeof content === 'object' ? content : null
    },
    editorialFacts () {
      const content = this.editorContent || {}
      const facts = [
        { key: 'summary', label: 'SEO summary', value: content.seoSummary, wide: true },
        { key: 'tier', label: 'Tier', value: content.tier },
        { key: 'released', label: 'Released', value: content.releasedDate || content.releasedYear },
        { key: 'discontinued', label: 'Discontinued', value: content.discontinued },
        { key: 'discontinued-year', label: 'Discontinued year', value: content.discontinuedYear },
        { key: 'best-for', label: 'Best for', value: Array.isArray(content.bestFor) ? content.bestFor.join(', ') : content.bestFor, wide: true },
        { key: 'note', label: 'Unique note', value: content.uniqueNote, wide: true },
        { key: 'original', label: 'Original description', value: content.originalDescription, wide: true },
        { key: 'official', label: 'Official page', value: content.officialUrl, url: content.officialUrl, wide: true }
      ]
      return facts.filter((fact) => fact.value !== null && fact.value !== undefined && String(fact.value).trim() !== '')
    },
    getSavedForm(){
      if(localStorage['savedundefinedForm'] && localStorage['savedundefinedForm'] !== '' ){
        return {type: 'undefined', item: localStorage['savedundefinedForm']}
      }
      if(localStorage['saved'+this.getType()+'Form'] && localStorage['saved'+this.getType()+'Form'] !== '') {
        return {type: this.getType(), item: localStorage['saved'+this.getType()+'Form'] }
      }
      else return ''
      
    },
    ...mapGetters([
          'getPartLoading',
          'getMessage',
          'getAttribs',
          'getItem',
          'getItemLoaded',
          'getFetchedItems'
        ]),
  },
  mounted() {
    if(this.checkEdit()){
      if(this.getItem){
       this.hydrateEditorFromPart(this.getItem)
      }
      this.loadModernPartDetail({ initial: true })
    }
  },
  methods: {
    ...mapActions([
      'setPartAttribs',
      'clearSavedForm',
      'fetchItems',
      'setItem',
      'setItems'
    ]),
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    catalogPartType (type) {
      const normalized = String(type || '').trim().toLowerCase()
      return normalized === 'graphics' ? 'gpu' : normalized
    },
    adminListType (type) {
      return this.catalogPartType(type) === 'gpu' ? 'graphics' : this.catalogPartType(type)
    },
    partIdentity (part) {
      if (!part) return ''
      return String(part.id || part.partId || part.part_id || '')
    },
    parseImageList (value) {
      if (!value || value === 'N/A') return []
      if (Array.isArray(value)) {
        return value.reduce((images, item) => images.concat(this.parseImageList(item)), [])
          .filter((image, index, list) => image && list.indexOf(image) === index)
      }
      if (typeof value === 'object') {
        const directUrl = value.url || value.src || value.imageUrl || value.image_url
        if (directUrl) return this.parseImageList(directUrl)
        return this.parseImageList(Object.values(value))
      }
      const text = String(value).trim()
      if (!text || text === 'N/A') return []
      try {
        const parsed = JSON.parse(text)
        if (parsed !== text) return this.parseImageList(parsed)
      } catch (error) {
        // Legacy rows sometimes contain a plain URL instead of JSON.
      }
      return [text]
    },
    rememberImageResolution (url, event) {
      const image = event && event.target
      if (!url || !image || !image.naturalWidth || !image.naturalHeight) return
      this.$set(this.imageDimensions, url, {
        width: image.naturalWidth,
        height: image.naturalHeight
      })
    },
    rememberImageResolutionError (url) {
      if (!url) return
      this.$set(this.imageDimensions, url, { error: true })
    },
    imageResolution (url) {
      const dimensions = this.imageDimensions[url]
      if (dimensions && dimensions.error) return 'Size unknown'
      if (!dimensions || !dimensions.width || !dimensions.height) return 'Measuring...'
      return `${dimensions.width} × ${dimensions.height}`
    },
    parseRecord (value) {
      if (!value) return {}
      if (typeof value === 'object' && !Array.isArray(value)) return value
      if (typeof value !== 'string') return {}
      try {
        const parsed = JSON.parse(value)
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
      } catch (error) {
        return {}
      }
    },
    normalizeModernPart (part) {
      const source = part && typeof part === 'object' ? part : {}
      const general = this.parseRecord(source.general)
      const specs = this.parseRecord(source.specs || source.specifications)
      const compat = this.parseRecord(source.compat || source.compats || source.compatibility)
      const media = this.parseRecord(source.media)
      return Object.assign({}, general, source, {
        id: source.id || source.partId || source.part_id || general.id || this.$route.query.id,
        type: source.type || general.type || this.$route.query.type,
        specs,
        compat,
        imgUrl: source.imgUrl !== undefined ? source.imgUrl :
          (source.images !== undefined ? source.images :
            (source.imageUrls !== undefined ? source.imageUrls :
              (source.image_urls !== undefined ? source.image_urls : (media.images || general.imgUrl)))),
        bullets: source.bullets !== undefined ? source.bullets : general.bullets
      })
    },
    detailPayload (data) {
      if (data && data.data && (data.data.part || data.data.maintenanceActions)) return data.data
      return data || {}
    },
    async loadModernPartDetail (options) {
      if (!this.checkEdit()) return
      const initial = !options || options.initial !== false
      const id = this.modernPartId
      const type = this.modernPartType
      if (!id || !type) {
        this.modernDetailLoading = false
        this.modernDetailRefreshing = false
        this.modernDetailError = 'This edit route is missing the part ID or category. Return to All Parts and open it again.'
        return
      }

      const requestId = ++this.modernDetailRequestId
      if (initial) this.modernDetailLoading = true
      else this.modernDetailRefreshing = true
      this.modernDetailError = ''
      try {
        const { data } = await axios.get(
          `${apiDomain}catalog-parts/${encodeURIComponent(type)}/${encodeURIComponent(id)}`,
          { headers: this.authHeaders() }
        )
        if (requestId !== this.modernDetailRequestId) return
        const payload = this.detailPayload(data)
        const normalized = this.normalizeModernPart(payload.part || payload.catalogPart || payload.catalog_part)
        this.modernPart = normalized
        this.partEvaluation = payload.evaluation || normalized.evaluation || null
        this.maintenanceActions = Array.isArray(payload.maintenanceActions)
          ? payload.maintenanceActions
          : (Array.isArray(payload.maintenance_actions) ? payload.maintenance_actions : [])
        this.setItem(normalized)
        this.hydrateEditorFromPart(normalized)
      } catch (error) {
        if (requestId !== this.modernDetailRequestId) return
        this.modernDetailError = (error.response && error.response.data &&
          (error.response.data.message || error.response.data.error)) || error.message || 'The part detail request failed.'
      } finally {
        if (requestId === this.modernDetailRequestId) {
          this.modernDetailLoading = false
          this.modernDetailRefreshing = false
        }
      }
    },
    hydrateEditorFromPart (part) {
      if (!part) return
      this.images = this.parseImageList(part.imgUrl !== undefined ? part.imgUrl : part.images)
      this.bullets = normalizeBulletList(part.bullets)
      this.$nextTick(() => this.assignInput(part))
    },
    async handleMaintenanceCompleted (event) {
      await this.loadModernPartDetail({ initial: false })
      if (this.modernDetailError) {
        if (event && typeof event.complete === 'function') event.complete(new Error(this.modernDetailError))
        return
      }
      const id = String(this.modernPartId)
      const currentItems = this.getFetchedItems || []
      if (this.modernPart && currentItems.length) {
        const images = this.parseImageList(this.modernPart.imgUrl)
        const mergedItems = currentItems.map((item) => {
          if (this.partIdentity(item) !== id) return item
          return Object.assign({}, item, this.modernPart, { mainImg: images[0] || item.mainImg || '' })
        })
        this.setItems(mergedItems)
      }
      try {
        const listType = this.adminListType(this.modernPartType)
        await this.fetchItems({
          type: listType,
          includeArchived: this.$route.query.showArchived === '1'
        })
        const refreshed = (this.getFetchedItems || []).find((item) => this.partIdentity(item) === id)
        if (refreshed) this.setItem(refreshed)
      } catch (error) {
        // Detail data is already current; a list refresh failure should not hide it.
      }
      if (event && typeof event.complete === 'function') event.complete()
    },
    amazonLink(asin){
      return amazonProductUrl(asin)
    },
    fieldLabel(field){
      return String(field || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase())
    },
    prepareModel(type, index){
      if(type == 'general'){
        this.generalValues[index] = ''
      }
      return

    },
    clearedTrue(){
      this.cleared = true
    },
    dropdown(type, field){
      var result = []
      const commonDropdowns = {
        is_available: [1, 0],
        availability_status: ['available', 'unavailable', 'out_of_stock', 'unknown']
      }
      if(Object.prototype.hasOwnProperty.call(commonDropdowns, field)){
        return commonDropdowns[field]
      }
      var storageDropdowns = 
        {
          'Disk_Type':  ['HDD', 'SSD'],
          'size':       ['3.5', '2.5', 'M.2'],
          'compat_interface': ['PCI-E', 'SATA'],
          'hardware_interface': ['PCI-E', 'SATA'],
          
        }
      
      if(type == 'storage'){
        // console.log('checking field ', Object.keys(storageDropdowns) )
        if(Object.keys(storageDropdowns).includes(field)){
          result = storageDropdowns[field]
        }
      }
      // console.log("DROPDOWN RESULT", result, ' for type ', type)
      return result
    },
    isLongTextField(field){
      return ['description', 'features', 'availability_reason', 'release_date_note'].indexOf(field) >= 0
    },
    scrollToTop() {
      window.scrollTo(0,0);
    },
    loadSavedForm(type){
      console.log("Load started with type: ", type)
      var savedItemForm = 'saved'+type+'Form'
      var savedData
      if(localStorage && localStorage[savedItemForm] && localStorage[savedItemForm] !== ''){
        console.log("found saved ", type, localStorage[savedItemForm])
        savedData = JSON.parse(localStorage[savedItemForm])
        console.log('prepared saved data ', savedData)
        var fieldNames = _.cloneDeep(this.getAttribs)
        console.log("prepared fieldNames ", fieldNames)
        var savedDataKeys = Object.keys(savedData)
        console.log("prepared saved data field names list ", savedDataKeys)
        if(savedDataKeys.length > 1){
          // Parsing from JSON 
          if(savedDataKeys.includes('imgUrl')){
            if(typeof(savedData.imgUrl) == 'string'){
              savedData.imgUrl = JSON.parse(savedData.imgUrl)
            }
            this.images = savedData.imgUrl
          }
          if(savedDataKeys.includes('bullets')){
            this.bullets = normalizeBulletList(savedData.bullets)
          }
          savedDataKeys.map(item => {
            console.log("FORMS HERE ", this.forms)
            console.log("if " + fieldNames.general + "includes ", item)
            if(fieldNames.general.includes(item)){
              
              for(let i = 0; i< this.forms.attribs.general.length; i++){
                if(this.forms.attribs.general[i].includes(item)){
                  this.generalValues[i] = savedData[item]
                  // console.log('added to general values ', savedData[item])
                }
              }
            }
            if(fieldNames.specifications.includes(item.replace(/_/gi, ' '))){
              for(let i = 0; i< this.forms.attribs.specifications.length; i++){
                if(this.forms.attribs.specifications[i].includes(item.replace(/_/gi, ' '))){
                  this.specValues[i] = savedData[item]
                  // console.log('added to spec values ', savedData[item])
                }
              }
            }
            if(fieldNames.compats.includes(item)){
              for(let i = 0; i< this.forms.attribs.compats.length; i++){
                if(this.forms.attribs.compats[i].includes(item)){
                  this.compatsValues[i] = savedData[item]
                  // console.log('added to compat values ', this.compatsValues[i])
                }
              }
            }
          })
        }
      }
      this.recovered = true
      console.log("RECOVERED VALUES: ", this.generalValues, this.specValues, this.compatsValues)
    },
    showLocalStorage(){
      console.log(localStorage)
    },
    updateRefField(field, value){
      this.$refs[field][0].value = value
      console.log("field ", field)
      console.log("value ", value)
    },
    testData(){
          localStorage.setItem('savedundefinedForm', `
      {
        "type": "undefined",
        "item": {
          "imgUrl": [
            "https://m.media-amazon.com/images/I/91MFwJy3syL._AC_SX450_.jpg",
            "https://m.media-amazon.com/images/I/91MFwJy3syL._AC_SL1500_.jpg",
            "https://m.media-amazon.com/images/I/911RlXmIDXL._AC_SL1500_.jpg",
            "https://m.media-amazon.com/images/I/91XOd0Dt6dL._AC_SL1500_.jpg",
            "https://m.media-amazon.com/images/I/91XPiIhnQ9L._AC_SL1500_.jpg",
            "https://m.media-amazon.com/images/I/91PcyFusP5L._AC_SL1500_.jpg",
            "https://m.media-amazon.com/images/I/91SEak+i+2L._AC_SL1500_.jpg",
            "https://m.media-amazon.com/images/I/81fGYlFQ1DL._AC_SL1500_.jpg",
            "https://m.media-amazon.com/images/I/A1rssquyn5L._AC_SL1500_.jpg",
            "https://m.media-amazon.com/images/I/91VVTruXnDL._AC_SL1500_.jpg",
            "https://m.media-amazon.com/images/I/91Es-c5x9fL._AC_SL1500_.jpg",
            "https://m.media-amazon.com/images/I/91YLvcQOJML._AC_SL1500_.jpg"
          ],
          "bullets": [
            "Supports 11th and 10th Gen Intel Core/Pentium/Celeron processors for LGA 1200 Socket",
            "Supports DDR4 Memory, up to 5333(OC) MHz",
            "Lightning Fast Game experience: PCIe 4.0, Lightning Gen 4 x4 M.2, USB 3.2 Gen 2x2",
            "Enhanced Power Design: 16+1+1 Duet Rail Power System, dual 8-pin CPU power connectors, Core Boost, DDR4 Boost",
            "Premium Thermal Solution: Enlarged heatsink with heat-pipe, MOSFET thermal pads rated for 7W/mk, additional choke thermal pads and 3x M.2 Shield Frozr are built for high performance system and non-stop gaming experience"
          ],
          "asin": "B08WBW8DB7",
          "type": "motherboard",
          "brand": "MSI",
          "model": "MPG Z590 Gaming Carbon WiFi",
          "description": "MSI MPG Z590 Gaming Carbon WiFi Gaming Motherboard (ATX, 11th/10th Gen Intel Core, LGA 1200 Socket, DDR4, PCIe 4, CFX, M.2 Slots, USB 3.2 Gen 2, Wi-Fi 6E, DP/HDMI, Mystic Light RGB)",
          "form_factor": "ATX",
          "amzn_price": "$269.99",
          "Compatible_Memory": "DDR4",
          "Socket": "LGA 1200",
          "WiFi": "Yes",
          "Integrated_Graphics": "Yes",
          "USB": "3.2",
          "Max_Memory_Size": "128",
          "ECC_Support": "Yes",
          "processor_generation": "10 11",
          "socket": "LGA 1200",
          "m_2": "SATA/PCIe",
          "M_2_Slot": "3",
          "ram": "DDR4",
          "ram_speed": "3200 MHz",
          "ram_slots": "4",
          "pci_e": "4.0",
          "sata_slots": "6",
          "sata_speed": "6Gb/s",
          "pci_e_slots": "3",
          "tdp": "55",
          "nvme": "Yes",
          "pci_e_x1": "2",
          "pci_e_x4": "0",
          "pci_e_x8": "0",
          "pci_e_x16": "3",
          "cpu_brand": "Intel"
        }
      }
    `)
    },
    getType(){
      this.type = this.$route.query.type
      return this.$route.query.type
    },
    saveForm(){
      var attribs = {}
      attribs.general = this.generalValues
      attribs.specifications = this.specValues
      attribs.compats = this.compatsValues
      if(attribs.general){
        console.log('saving form ', attribs.general)
        attribs.general["imgUrl"] = JSON.stringify(this.images)
        attribs.general["bullets"] = JSON.stringify(this.bullets)
        if(this.$route.name == 'Edit Part'){
          attribs.general["id"] = this.modernPartId
        }
        for(var i = 0; i< this.forms.attribs.general.length; i++){
          attribs.general[this.forms.attribs.general[i].replace(/ |-/gi, '_')] = attribs.general[i]
          delete attribs.general[i]
        }
        for(var j = 0; j< this.forms.attribs.specifications.length; j++){
          attribs.specifications[this.forms.attribs.specifications[j].replace(/ |-/gi, '_')] = attribs.specifications[j]
          delete attribs.specifications[j]
        }
        for(var k = 0; k< this.forms.attribs.compats.length; k++){
          attribs.compats[this.forms.attribs.compats[k].replace(/ |-/gi, '_')] = attribs.compats[k]
          delete attribs.compats[k]
        }
        var fullAttribs = {
          ...attribs.general,
          ...attribs.specifications,
          ...attribs.compats
        }
          var savedFormName = 'saved'+this.getType()+'Form'
          localStorage.setItem(savedFormName, JSON.stringify(fullAttribs))
          console.log('item form was saved ', localStorage)
        return 
      }
    },
    makeMain(images, index){
      if(images && index){
        var tmpImages = [...images]
        var mainImage = images[index]
        tmpImages.splice([index], 1)
        tmpImages.unshift(mainImage)
        this.images = [...tmpImages]
        console.log(this.images)
        console.log('made main')
      }
    },
    spliceImg(index){
      this.images.splice(index, 1)
      console.log('spliced array', "index removed:", index,"  ", this.images)
    },
    spliceBullet(index){
      this.bullets.splice(index, 1)
      console.log('spliced array', "index removed:", index,"  ", this.bullets)
    },
    imgInputFocus(){
      this.$refs.imgInput.focus()
    },
    bulletInputFocus(){
      this.$refs.bulletInput.focus()
    },
    addImg(imgUrl){
      if(imgUrl !== undefined && imgUrl.length > 3) {
        this.images.push(imgUrl)
        console.log('url added to array:', this.images)
        this.imgUrl = undefined
        this.$refs.imgInput.value = ''
      }
      else console.log('bad url')
      this.imgUrl = undefined
    },
    addBullet(bullet){
      if(bullet !== undefined && bullet.length > 3) {
        this.bullets.push(bullet)
        console.log('bullet added to array:', this.bullets)
        this.bullet = undefined
        this.$refs.bulletInput.value = ''
        this.bulletInputFocus()
      }
      else console.log('bad url')
      this.bullet = undefined
    },
    checkEdit(){
      if(this.$route.name == 'Edit Part'){
        return true
      }
      else return false
    },
    show(){
      console.log('generalValues: ', this.generalValues)
      console.log('specValues: ', this.specValues)
      console.log('compatsValues: ', this.compatsValues)
    },
    getAttribsList(){
      console.log('generalValues: ', this.generalValues)
      console.log('specValues: ', this.specValues)
      console.log('compatsValues: ', this.compatsValues)
      var attribs = {}
      attribs.general = {...this.generalValues}
      attribs.specifications = {...this.specValues}
      attribs.compats = {...this.compatsValues}
      if(attribs.general){
        console.log('getattribs values', this.generalValues, this.specValues, this.compatsValues)
        // console.log("ATTRIBS ", attribs)
        // console.log('THIS.FORMS ', this.forms)
        for(var i = 0; i< this.forms.attribs.general.length; i++){
          // console.log("this.forms.attribs.general[i] ", this.forms.attribs.general[i])
          var formField = this.forms.attribs.general[i].replace(/ |-/gi, '_')
          // console.log("FORM FIELD MATCH ", formField, attribs.general)
          attribs.general[formField] = attribs.general[i]
          delete attribs.general[i]
          this.updateRefField(
            "input_general_"+ formField,
            attribs.general[formField]
          )
          // console.log("CHECKING HERE ", attribs.general)
        }
        for(var j = 0; j< this.forms.attribs.specifications.length; j++){
          // console.log('assigning form field to attribs.specifications', this.forms.attribs.specifications[j])
          attribs.specifications[this.forms.attribs.specifications[j].replace(/ |-/gi, '_')] = attribs.specifications[j] || ''
          delete attribs.specifications[j]
        }
        for(var k = 0; k< this.forms.attribs.compats.length; k++){
          attribs.compats[this.forms.attribs.compats[k]] = attribs.compats[k]
          delete attribs.compats[k]
        }
        if(this.$route.name == 'Edit Part'){
          attribs.general["id"] = this.modernPartId
        }
        attribs.general["imgUrl"] = JSON.stringify(this.images)
        attribs.general["bullets"] = JSON.stringify(this.bullets)
        attribs.general["type"] = this.getType()
        // console.log("ATTRIBS READY ", attribs)
        var fullAttribs = {
          ...attribs.general,
          ...attribs.specifications,
          ...attribs.compats
        }
        // console.log("ASSIGN REFS")
        //         // GENERAL
        // for(const field in attribs.general){
        //   for (const ref in this.$refs){
        //     if(ref.toLowerCase().replace(/ |-/gi, '_').toLowerCase().includes(field.toLowerCase()) && field !== 'specs' && field !== 'compat'){
        //       if( this.$refs[ref][0]){
        //         this.$refs[ref][0].value = attribs.general[field.replace(/ |-/gi, '_')]
        //       }
        //       this.forms.attribs.general[field.replace(/ |-/gi, '_')] = attribs.general[field.replace(/ |-/gi, '_')]
        //       // console.log('general asigning value to field ', this.$refs[ref][0])
        //     }
        //   }
        // }
        // // SPECS
        // for(const field in attribs.specifications){
        //   // console.log('assigning specs ', field)
        //   for (const ref in this.$refs){
        //     if(ref.toLowerCase().replace(/ |-/gi, '_').toLowerCase().includes(field.replace(/ |-/gi, '_').toLowerCase()) && !field.includes('specs')){
        //       console.log('match found, value ', attribs.specifications[field.replace(/ |-/gi, '_')])
        //       if( this.$refs[ref][0]){
        //         this.$refs[ref][0].value = attribs.specifications[field.replace(/ |-/gi, '_')]
        //       }
        //       this.forms.attribs.specifications[field.replace(/ |-/gi, '_')] = attribs.specifications[field.replace(/ |-/gi, '_')]
        //       // console.log('specifications asigned value to field ', this.$refs[ref][0].value,  this.forms.attribs.specifications[field.replace(/ |-/gi, '_')])
        //     }
        //   }
        // }
        // //COMPATS
        // for(const field in attribs.compats){
        //   for (const ref in this.$refs){
        //     if(ref.toLowerCase().replace(/ |-/gi, '_').toLowerCase().includes(field.replace(/ |-/gi, '_').toLowerCase()) && !field.includes('compat')){
        //       if( this.$refs[ref][0]){
        //         this.$refs[ref][0].value = attribs.compats.compat[field.replace(/ |-/gi, '_')]
        //       }
        //       this.forms.attribs.compats[field.replace(/ |-/gi, '_')] = attribs.compats[field.replace(/ |-/gi, '_')]
        //       if(ref.includes('tdp') && !field.includes('compat')){
        //         console.log("tdp!!! ", field.replace(/ |-/gi, '_'), ' field: ', field)
        //       }
        //       // console.log('compats asigning value to field ', this.$refs[ref][0].value)
        //     }
        //   }
        // }
        // this.$forceUpdate()



        console.log('returning full attribs ', fullAttribs)

        return JSON.stringify(fullAttribs)
      }
    },
    async fetchProductInfo(asin){
      console.log('refs ', this.$refs)
      console.log('this.forms ', this.forms)
      console.log('values ', this.generalValues, this.specValues, this.compatsValues)
      this.fetchingAmzn = true
      var productData = null
      await axios({
        method: 'GET',
        url: `${SERVER_PATH}scrape/product?asin=${asin}`
      }).then((response) => {productData = response; this.fetchingAmzn = false})
      .catch((error) => {console.log(error); this.fetchingAmzn = false})
      console.log('fetched amazon data: ', productData)
      if(productData.data.mainImg && productData.data.mainImg !== 'could not catch'){
        this.images.push(productData.data.mainImg)
      }
      if(productData.data.bullets && productData.data.bullets !== 'could not catch' && productData.data.bullets.length >0){
        productData.data.bullets.map(item => {
          this.bullets.push(item)
        })
      }
      for(var i = 0; i< this.forms.attribs.general.length; i++){
        if(this.forms.attribs.general[i] == 'amzn_price'){
           this.generalValues[i] = productData.data.price
        }
        if(this.forms.attribs.general[i] == 'description'){
           this.generalValues[i] = productData.data.title
        }
        if(this.forms.attribs.general[i] == 'asin'){
           this.generalValues[i] = productData.data.asin
        }
        if(this.forms.attribs.general[i] == 'type'){
          console.log('type ', this.getType())
           this.generalValues[i] = this.getType()
        }
        if(this.forms.attribs.general[i] == 'brand'){
           this.generalValues[i] = productData.data.brand
        }
      }
      for(var j = 0; i<this.forms.attribs.specifications.length; i++){
        if(this.forms.attribs.specifications[j] == 'brand'){
          this.specValues[j] = productData.data.brand
        }        
      }
      for(var k = 0; k<this.forms.attribs.compats.length; k++){
        if(this.forms.attribs.compats[k] == 'brand'){
          this.compatsValues[k] = productData.data.brand
        }
      }
      console.log(this.$refs)
      for (const ref in this.$refs){
        if(ref.toLowerCase().includes('price')){
          this.$refs[ref][0].value = productData.data.price
        }
        if(ref.toLowerCase().includes('description')){
          this.$refs[ref][0].value = productData.data.title
        }
        if(ref.toLowerCase().includes('asin')){
          this.$refs[ref][0].value = productData.data.asin
        }
        if(ref.toLowerCase().includes('type')){
          this.$refs[ref][0].value = this.getType()
        }
        if(ref.toLowerCase().includes('brand')){
          console.log('ref brand ', this.$refs[ref])
          this.$refs[ref][0].value = productData.data.brand
        }
        // if(ref.toLowerCase().includes('cpu_brand')){
        //   if(productData.data.title.toLowerCase().includes('intel')){
        //     this.$refs[ref][0].value = 'Intel'
        //   }
        //   else if(productData.data.title.toLowerCase().includes('amd')){
        //     this.$refs[ref][0].value = 'AMD'
        //   }
        // }
      }
      console.log('refs ', this.$refs)
      console.log('this.forms ', this.forms)
      console.log('fetched product price: ', productData.data.price)
    },
    showAsin(){
      // if(this.generalValues)
      // console.log(this.generalValues[0])
    },
    print(index){
      return console.log(Object.values(this.settings.options)[index])
    },
    async handleProcessorSubmit({clone}) {
      var cloneMode = false
      if(clone == true){
        cloneMode = true
      }
      this.scrollToTop()
      // if(this.$route.name == 'Add Part'){
      //   this.saveForm()
      // }
			const postData = this.getAttribsList()
      var savedFormName = 'saved'+this.getType()+'Form'
      localStorage.setItem(savedFormName, JSON.stringify(postData))
      // Sending Request
      console.log('sending this')
      console.log(postData)
      if(this.$route.name == 'Add Part'){
        try{
          await this.$store.dispatch('addItem', JSON.parse(postData))
        }catch(e){
          console.log("Error: ")
          console.log(e)
        }
      }
      if(this.$route.name == 'Edit Part' && cloneMode == false){
        try{
          await this.$store.dispatch('editItem', postData)
        }catch(e){
          console.log("Error: ")
          console.log(e)
        }
      }
      if(this.$route.name == 'Edit Part' && cloneMode == true){
        try{
          var tempData = JSON.parse(postData)
          tempData.asin = 'CLONE '+tempData.asin
          tempData.description = 'CLONE '+tempData.description
          tempData.brand = 'CLONE '+tempData.brand
          JSON.parse(postData).asin = 'CLONE'
          await this.$store.dispatch('addItem', tempData)
        }catch(e){
          console.log("Error: ")
          console.log(e)
        }
      }
    },
    assignInput(source){
      if(this.$route.name !== "Edit Part" || !source || !this.forms.attribs) return
      const specs = source.specs && typeof source.specs === 'object' ? source.specs : {}
      const compat = source.compat && typeof source.compat === 'object' ? source.compat : {}
      this.generalValues = this.forms.attribs.general.map((field) => source[field.replace(/ |-/gi, '_')])
      this.specValues = this.forms.attribs.specifications.map((field) => specs[field.replace(/ |-/gi, '_')])
      this.compatsValues = this.forms.attribs.compats.map((field) => compat[field.replace(/ |-/gi, '_')])
      this.type = source.type || this.getType()
    }
  },
  beforeMount(){
    this.forms.attribs = this.getAttribs;
  },
  updated(){
  },
  created(){
    this.type = this.getType()
    console.log(this.$route.name)
    if(this.$route.name !== 'Edit Part'){
      this.images = []
      this.bullets = []
      this.generalValues = []
      this.specValues = []
      this.compatsValues = []
    }
  }
}
</script>
<style scoped>
.part-editor-page {
	position: relative;
	color: #cfd2da;
}

.part-editor-actions {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	margin: 1rem 0;
}

.part-editor-primary-link,
.part-editor-secondary-button,
.gc-button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0.45rem;
	min-height: 38px;
	padding: 0 1rem;
	border-radius: 6px;
	cursor: pointer;
	font-size: 13px;
	font-weight: 800;
	letter-spacing: 0.3px;
	line-height: 1;
	text-decoration: none;
	text-transform: uppercase;
	transition: background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease;
}

.part-editor-primary-link,
.gc-button {
	border: 1px solid #16b4ed;
	background: linear-gradient(180deg, rgba(25, 151, 198, 0.34), rgba(12, 66, 88, 0.64));
	box-shadow:
		0 0 24px rgba(25, 151, 198, 0.13),
		inset 0 0 18px rgba(25, 151, 198, 0.12);
	color: #f4f8fb;
}

.part-editor-secondary-button {
	border: 1px solid rgba(25, 151, 198, 0.64);
	background: rgba(25, 151, 198, 0.06);
	color: #cfd2da;
	margin-left: auto;
}

.part-editor-primary-link:hover,
.part-editor-primary-link:focus,
.gc-button:hover,
.gc-button:focus,
.part-editor-secondary-button:hover,
.part-editor-secondary-button:focus {
	border-color: #1997c6;
	background: rgba(25, 151, 198, 0.16);
	color: #ffffff !important;
	text-decoration: none;
}

.part-editor-form {
	width: 100%;
}

.part-editor-main {
	overflow-x: hidden;
}

.part-editor-fieldset {
	min-width: 0;
	margin: 0;
	padding: 0;
	border: 0;
}

.block {
	position: relative;
	margin: 1rem 0;
	padding: 1.15rem 1.25rem;
	border: 1px solid rgba(67, 72, 87, 0.82);
	border-radius: 6px;
	background: rgba(8, 10, 15, 0.36);
	box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.018);
}

.block h4 {
	margin: 0 0 1rem;
	padding: 0;
	color: #f4f8fb !important;
	font-size: 20px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: 1.2;
	text-align: left;
	text-transform: capitalize;
}

.images {
	display: flex;
	align-items: center;
	gap: 0.7rem;
	margin: 0 !important;
	padding: 0;
}

.img-div {
	position: relative;
	display: inline-flex;
	align-items: flex-start;
	justify-content: flex-end;
	width: 120px;
	height: 120px;
	margin: 0.35rem 0.65rem 1.4rem 0;
	border: 1px solid rgba(67, 72, 87, 0.86);
	border-radius: 6px;
	background-color: #f4f8fb;
	background-position: center;
	background-repeat: no-repeat;
	background-size: contain;
	overflow: hidden;
	transition: border-color 160ms ease, box-shadow 160ms ease;
}

.img-div:hover {
	border-color: rgba(25, 151, 198, 0.82);
	box-shadow: 0 0 22px rgba(25, 151, 198, 0.12);
	cursor: pointer;
}

.img-empty {
	align-items: center;
	justify-content: center;
	background: rgba(6, 8, 12, 0.72);
}

.plus {
	color: #9198aa !important;
	font-size: 48px;
	line-height: 1;
	text-decoration: none;
}

.img-empty:hover .plus {
	color: #1997c6 !important;
}

.make-main-button {
	position: absolute;
	right: 0;
	bottom: 0;
	left: 0;
	margin: 0 !important;
	padding: 0.28rem 0.4rem;
	border-top: 1px solid rgba(25, 151, 198, 0.72);
	background: rgba(8, 10, 15, 0.92);
	color: #1997c6 !important;
	font-size: 12px !important;
	font-weight: 700;
	text-align: center;
	text-decoration: none;
	text-transform: uppercase;
}

.make-main-button:hover {
	color: #ffffff !important;
}

.cross {
	color: #ff5b6c !important;
	cursor: pointer;
	font-size: 13px;
	font-weight: 800;
	text-decoration: none;
}

.cross:hover {
	color: #ffffff !important;
}

.block ul {
	margin: 0 0 0.65rem;
	padding: 0.58rem 0.8rem;
	border: 1px solid rgba(67, 72, 87, 0.82);
	border-radius: 6px;
	background: rgba(6, 8, 12, 0.54);
	color: #cfd2da;
	list-style-position: inside;
	text-align: left;
}

.block ul:hover {
	border-color: rgba(25, 151, 198, 0.45);
	background: rgba(25, 151, 198, 0.06);
}

.block ul li {
	color: #cfd2da !important;
	font-size: 15px;
	line-height: 1.35;
}

.block ul li a {
	float: right !important;
	margin-top: 1px;
	font-size: 12px !important;
}

li::marker {
	color: #1997c6;
}

.form-group {
	display: grid;
	grid-template-columns: minmax(150px, 0.32fr) minmax(0, 1fr);
	align-items: center;
	gap: 1rem;
	margin: 0 0 0.75rem;
}

label {
	margin: 0;
	padding: 0;
	color: #9198aa;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.4px;
	line-height: 1.2;
	text-align: left;
	text-transform: uppercase;
}

.form-control,
.url-input,
select {
	width: 100%;
	min-height: 40px;
	margin: 0;
	padding: 0.58rem 0.7rem;
	border: 1px solid rgba(67, 72, 87, 0.96);
	border-radius: 6px;
	outline: none;
	background: rgba(6, 8, 12, 0.76);
	color: #e5e8ef;
	font-size: 15px;
	font-weight: 400;
	letter-spacing: 0;
	transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.form-control::placeholder,
.url-input::placeholder {
	color: rgba(145, 152, 170, 0.7);
}

.form-control:hover,
.url-input:hover,
select:hover {
	border-color: rgba(25, 151, 198, 0.55);
}

.form-control:focus,
.url-input:focus,
select:focus {
	border-color: #1997c6;
	background: rgba(8, 10, 15, 0.98);
	box-shadow: 0 0 0 3px rgba(25, 151, 198, 0.11);
	color: #e5e8ef;
}

.form-80-percent-width {
	max-width: none;
	margin: 0;
}

.gc-button li {
	color: inherit;
	list-style: none;
}

.submit-button {
	float: none;
	min-width: 170px;
	margin: 0.6rem 0 0 0;
	margin-left: auto;
}

.notification {
	position: absolute;
	right: 0;
	left: 0;
	z-index: 1;
	padding: 0.85rem 1rem;
	border: 1px solid rgba(25, 151, 198, 0.72);
	border-radius: 6px;
	background: rgba(8, 10, 15, 0.96);
	color: #e5e8ef;
	font-size: 14px;
	font-weight: 700;
	text-align: left;
}

.notification-options {
	float: right;
}

.notification-options a {
	margin-right: 15px;
	color: #1997c6;
	cursor: pointer;
}

@media screen and (max-width: 860px) {
	.part-editor-actions,
	.images {
		align-items: stretch;
		flex-direction: column;
	}

	.form-group {
		grid-template-columns: 1fr;
		gap: 0.4rem;
	}

	.submit-button,
	.part-editor-primary-link,
	.part-editor-secondary-button,
	.gc-button {
		width: 100%;
	}
}
</style>

<style scoped>
.part-editor-page {
  position: relative;
  padding: 14px 0 0;
  color: #cfd2da;
}

.part-editor-overview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 12px;
  padding: 14px 16px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 8px;
  background:
    radial-gradient(circle at 4% 0%, rgba(25, 151, 198, 0.1), transparent 32%),
    linear-gradient(145deg, rgba(14, 21, 32, 0.76), rgba(6, 10, 17, 0.62));
}

.part-editor-overview-copy {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.part-editor-kicker,
.part-editor-section-kicker {
  color: #20bff3;
  font-size: 8pt;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.part-editor-overview-copy > strong {
  overflow: hidden;
  color: #eef3f9;
  font-size: 13pt;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.part-editor-overview-copy > small {
  color: #7f899b;
  font-size: 8.7pt;
  line-height: 1.4;
}

.part-editor-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
}

.part-editor-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.58);
  color: #c9d1de;
  cursor: pointer;
  font-family: inherit;
  font-size: 8.5pt;
  font-weight: 900;
  letter-spacing: 0.03em;
  line-height: 1;
  text-decoration: none;
  text-transform: uppercase;
  transition: 0.15s ease;
}

.part-editor-button:hover:not(:disabled),
.part-editor-button:focus:not(:disabled) {
  border-color: rgba(25, 151, 198, 0.75);
  background: rgba(25, 151, 198, 0.12);
  color: #f4f8fb;
  text-decoration: none;
}

.part-editor-button:disabled {
  cursor: wait;
  opacity: 0.55;
}

.part-editor-button-primary {
  border-color: rgba(22, 180, 237, 0.72);
  background: rgba(25, 151, 198, 0.14);
  color: #aeeaff;
}

.part-editor-main,
.part-editor-fieldset {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.part-editor-assets-grid,
.part-editor-form-groups {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.part-editor-form-groups {
  margin-top: 12px;
}

.part-editor-card {
  min-width: 0;
  margin: 0;
  padding: 14px;
  border: 1px solid rgba(82, 94, 121, 0.76);
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(11, 16, 25, 0.88), rgba(5, 8, 14, 0.76));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.035),
    0 10px 24px rgba(0, 0, 0, 0.14);
}

.part-editor-section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.52);
}

.part-editor-section-head > div {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.part-editor-section-head h2 {
  margin: 0;
  color: #e6ebf2;
  font-size: 11.5pt;
  font-weight: 700;
  line-height: 1.2;
}

.part-editor-count {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 8px;
  border: 1px solid rgba(67, 72, 87, 0.66);
  border-radius: 999px;
  background: rgba(8, 11, 17, 0.42);
  color: #8792a5;
  font-size: 7.5pt;
  font-weight: 900;
  white-space: nowrap;
}

.part-editor-media-card,
.part-editor-bullets-card {
  display: flex;
  flex-direction: column;
  min-height: 264px;
}

.part-editor-image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(86px, 1fr));
  gap: 9px;
  margin-bottom: 12px;
}

.part-editor-image {
  position: relative;
  min-width: 0;
  height: 98px;
  overflow: hidden;
  border: 1px solid rgba(67, 72, 87, 0.86);
  border-radius: 7px;
  background-color: #f4f8fb;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.2);
  transition: 0.15s ease;
}

.part-editor-image:hover,
.part-editor-image.is-main {
  border-color: rgba(25, 151, 198, 0.76);
}

.part-editor-main-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 3px 6px;
  border-radius: 999px;
  background: rgba(8, 12, 19, 0.88);
  color: #8fe8c4;
  font-size: 7pt;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.part-editor-image-probe {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.part-editor-image-resolution {
  position: absolute;
  bottom: 6px;
  left: 6px;
  max-width: calc(100% - 12px);
  padding: 3px 6px;
  overflow: hidden;
  border: 1px solid rgba(67, 72, 87, 0.62);
  border-radius: 999px;
  background: rgba(8, 12, 19, 0.88);
  color: #c3ccda;
  font-size: 6.8pt;
  font-weight: 800;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.part-editor-image-resolution.with-action {
  bottom: 35px;
}

.part-editor-image-remove {
  position: absolute;
  top: 5px;
  right: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid rgba(232, 79, 106, 0.48);
  border-radius: 50%;
  background: rgba(36, 10, 18, 0.86);
  color: #f49aaa;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}

.part-editor-make-main {
  position: absolute;
  right: 5px;
  bottom: 5px;
  left: 5px;
  min-height: 24px;
  border: 1px solid rgba(25, 151, 198, 0.54);
  border-radius: 5px;
  background: rgba(8, 12, 19, 0.9);
  color: #9bdff5;
  cursor: pointer;
  font-size: 7pt;
  font-weight: 900;
  text-transform: uppercase;
}

.part-editor-empty-media,
.part-editor-empty-copy {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: 112px;
  margin-bottom: 12px;
  border: 1px dashed rgba(82, 94, 121, 0.68);
  border-radius: 7px;
  background: rgba(4, 7, 12, 0.3);
  color: #7f899b;
}

.part-editor-empty-media {
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
}

.part-editor-empty-media .icon {
  margin-bottom: 2px;
  color: #1997c6;
  font-size: 20px;
}

.part-editor-empty-media strong {
  color: #aeb7c7;
  font-size: 9pt;
}

.part-editor-empty-media small,
.part-editor-empty-copy {
  font-size: 8.3pt;
}

.part-editor-bullet-list {
  display: grid;
  gap: 7px;
  max-height: 202px;
  margin: 0 0 12px;
  padding: 0 4px 0 0;
  overflow: auto;
  list-style: none;
}

.part-editor-bullet-list li {
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr) 24px;
  gap: 8px;
  align-items: start;
  padding: 8px 8px 8px 10px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 6px;
  background: rgba(4, 7, 12, 0.38);
  color: #cbd2dd;
  font-size: 8.5pt;
  line-height: 1.38;
}

.part-editor-bullet-list li::before {
  color: #20bff3;
  content: "\2022";
  font-weight: 900;
}

.part-editor-bullet-list button {
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #e84f6a;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
}

.part-editor-inline-add {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  margin-top: auto;
}

.part-editor-group-general {
  grid-column: 1 / -1;
}

.part-editor-fields {
  display: grid;
  gap: 9px 12px;
}

.part-editor-fields-general {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.part-editor-field {
  display: grid;
  align-content: start;
  gap: 5px;
  min-width: 0;
}

.part-editor-field label {
  overflow: hidden;
  margin: 0;
  padding: 0;
  color: #99a4b8;
  font-size: 8pt;
  font-weight: 800;
  letter-spacing: 0.03em;
  line-height: 1.25;
  text-align: left;
  text-overflow: ellipsis;
  text-transform: none;
  white-space: nowrap;
}

.part-editor-page .form-control,
.part-editor-page select.form-control {
  width: 100%;
  height: 36px;
  min-height: 36px;
  margin: 0;
  padding: 0 9px;
  border: 1px solid rgba(67, 72, 87, 0.88);
  border-radius: 6px;
  outline: none;
  background: rgba(4, 7, 12, 0.72);
  color: #dfe5ef;
  font-family: inherit;
  font-size: 9pt;
  line-height: 1.35;
  transition: 0.14s ease;
}

.part-editor-page .form-control::placeholder {
  color: #596376;
}

.part-editor-page .form-control:hover {
  border-color: rgba(25, 151, 198, 0.5);
}

.part-editor-page .form-control:focus {
  border-color: rgba(25, 151, 198, 0.78);
  background: rgba(4, 7, 12, 0.94);
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.1);
}

.part-editor-page textarea.part-editor-textarea {
  height: auto;
  min-height: 74px;
  padding-top: 8px;
  padding-bottom: 8px;
  resize: vertical;
}

.part-editor-save-bar {
  position: sticky;
  bottom: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 12px 0 0;
  padding: 12px 14px;
  border: 1px solid rgba(67, 72, 87, 0.76);
  border-radius: 8px;
  background: rgba(8, 12, 19, 0.96);
  box-shadow: 0 -12px 28px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
}

.part-editor-save-bar > div {
  display: grid;
  gap: 2px;
}

.part-editor-save-bar strong {
  color: #dfe5ef;
  font-size: 9.5pt;
}

.part-editor-save-bar small {
  color: #7f899b;
  font-size: 8pt;
}

.part-editor-button-save {
  min-width: 142px;
  border-color: rgba(27, 201, 142, 0.62);
  background: rgba(27, 201, 142, 0.12);
  color: #9df0cc;
}

.part-editor-button-save:hover:not(:disabled),
.part-editor-button-save:focus:not(:disabled) {
  border-color: rgba(27, 201, 142, 0.86);
  background: rgba(27, 201, 142, 0.2);
  color: #dfffee;
}

.part-editor-loaded-content {
  min-width: 0;
}

.part-editor-detail-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  min-height: 180px;
  padding: 20px;
  border: 1px solid rgba(232, 79, 106, 0.54);
  border-radius: 8px;
  background: rgba(69, 18, 29, 0.18);
}

.part-editor-detail-error strong {
  color: #ffdbe2;
  font-size: 12pt;
}

.part-editor-detail-error p {
  margin: 5px 0 0;
  color: #d0a8b0;
  font-size: 9pt;
}

.part-editor-refreshing {
  margin: -3px 0 12px;
  color: #20bff3;
  font-size: 8pt;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-align: right;
  text-transform: uppercase;
}

.part-editor-evaluation-card {
  margin: 12px 0;
  min-width: 0;
}

.part-editor-editorial-card {
  margin: 12px 0;
}

.part-editor-readonly {
  padding: 3px 7px;
  border: 1px solid rgba(67, 72, 87, 0.68);
  border-radius: 999px;
  color: #7f899b;
  font-size: 7pt;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.part-editor-editorial-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.part-editor-editorial-grid article {
  min-width: 0;
  padding: 9px 10px;
  border: 1px solid rgba(67, 72, 87, 0.62);
  border-radius: 6px;
  background: rgba(4, 7, 12, 0.38);
}

.part-editor-editorial-grid article.is-wide {
  grid-column: span 2;
}

.part-editor-editorial-grid span,
.part-editor-editorial-grid strong,
.part-editor-editorial-grid a {
  display: block;
}

.part-editor-editorial-grid span {
  margin-bottom: 4px;
  color: #748095;
  font-size: 7pt;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.part-editor-editorial-grid strong,
.part-editor-editorial-grid a {
  color: #cfd8e6;
  font-size: 8.3pt;
  font-weight: 600;
  line-height: 1.42;
  overflow-wrap: anywhere;
}

.part-editor-editorial-grid a {
  color: #7ed9f5;
}

@media screen and (max-width: 960px) {
  .part-editor-assets-grid,
  .part-editor-form-groups {
    grid-template-columns: 1fr;
  }

  .part-editor-group-general {
    grid-column: auto;
  }

  .part-editor-editorial-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 640px) {
  .part-editor-overview,
  .part-editor-save-bar,
  .part-editor-detail-error {
    align-items: stretch;
    flex-direction: column;
  }

  .part-editor-actions,
  .part-editor-button,
  .part-editor-button-save {
    width: 100%;
  }

  .part-editor-fields-general,
  .part-editor-inline-add {
    grid-template-columns: 1fr;
  }

  .part-editor-image-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .part-editor-editorial-grid {
    grid-template-columns: 1fr;
  }

  .part-editor-editorial-grid article.is-wide {
    grid-column: auto;
  }

  .part-editor-save-bar {
    position: static;
  }
}
</style>
